import { useContext, useEffect, useMemo } from 'react'

import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { useIntl } from 'react-intl'
import sanitize from 'sanitize-html'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { Container } from 'components/atoms/container'
import { PremiumContentBanner } from 'components/atoms/premiumContentBanner'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { AnimationBanner } from 'components/molecules/animations/banner'
import { AnimationChart } from 'components/molecules/animations/chart'
import { HealthRendership } from 'components/molecules/animations/healthRendership'
import { MostViewedCategoriesChart } from 'components/molecules/animations/MostViewedCategoriesChart'
import { ObesityPercentage } from 'components/molecules/animations/obesity'
import { ArticleFeedback } from 'components/molecules/articleFeedback'
import { CollapsibleFaqs } from 'components/molecules/collapsibleFaqs'
import { Sources } from 'components/molecules/sources'
import ArticleActionButtonGroup from 'components/organisms/articleActionButtonGroup'
import { EmbeddedCommunity } from 'components/organisms/embededCommunity'
import { BabyPoopCard } from 'components/organisms/embededHeathToolCards/baby-poop'
import { BabyVaccinationCard } from 'components/organisms/embededHeathToolCards/baby-vaccination'
import { BabyGrowthCard } from 'components/organisms/embededHeathToolCards/embedBabyGrowth'
import { BMICardWrapper } from 'components/organisms/embededHeathToolCards/embedBMI'
import { BMRCardWrapper } from 'components/organisms/embededHeathToolCards/embedBMR'
import { DueDateCardWrapper } from 'components/organisms/embededHeathToolCards/embedDueDate'
import { OvulationCardWrapper } from 'components/organisms/embededHeathToolCards/embedOvulation'
import { PWGCardWrapper } from 'components/organisms/embededHeathToolCards/embedPwg'
import { THRCardWrapper } from 'components/organisms/embededHeathToolCards/embedTHR'
import { HealthConcern } from 'components/organisms/healthConcernCarousel'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { MastheadAd } from 'components/pages/article/components/MastheadAd'
import {
  StyledActionBarWrapper,
  StyledSuperContentWrapper,
} from 'components/pages/landingPage/index.styled'
import { FormattedPostItemContent } from 'components/templates/article/articleContents'
import { StyledArticleContentWrapperNoAmp } from 'components/templates/article/index.styled'
import { splitHtml2 } from 'components/templates/article/utils'
import { domainLocales } from 'domainLocales'
import { useMastheadAdEnable } from 'hooks/useMastheadAdEnable'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ENV, IS_SSOV2_ENABLED, SSO_URL } from 'interfaces/constants'
import {
  HealthToolType,
  LOCALE,
  Spotlight,
  SpotlightPageBanner,
} from 'interfaces/types'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'
import { useArticleStore } from 'state/article'
import { getAdTag, getCountryAdTag } from 'utils/ads'

export type Props = {
  isMobile: boolean
  spotlight: Spotlight
  isSponsored: boolean
}

type SpotlightContentFormatted = {
  html: string
  graphBarChart?: {
    direction: 'vertical' | 'horizontal'
  }
  healthRenderShip?: boolean
  obesity?: boolean
  mostViewedCategories?: boolean
  heathTool?: HealthToolType
  carousel?: {
    id: number
  }
  faqs?: boolean
} & FormattedPostItemContent

const _sanitize = (html: string) => {
  return sanitize(html, {
    allowedTags: false,
    allowedAttributes: false,
    allowVulnerableTags: true,
  })
}
const SpotlightSuperContentTemplate = ({
  isMobile,
  spotlight,
  isSponsored,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()

  const currentDomain = domainLocales[locale]

  const {
    state: { userInfo, isLoggedIn },
    dispatch,
  } = useContext(AppContext)

  const {
    bookmarkIds,
    setIsLiked,
    setIsDisliked,
    currentArticle,
    fontSize,
    addToBookmark,
    removeBookmark,
  } = useArticleStore(
    (state) => ({
      bookmarkIds: state.bookmarkIds,
      setIsLiked: state.setIsLiked,
      setIsDisliked: state.setIsDisliked,
      currentArticle: state.currentArticle,
      fontSize: state.fontSize,
      addToBookmark: state.addToBookmark,
      removeBookmark: state.removeBookmark,
    }),
    shallow,
  )

  useEffect(() => {
    const fetchReaction = async () => {
      const reactionResponse = isLoggedIn
        ? await callApiNew(
            PATHS.SSO.SSO_FEEDBACK_USER,
            {
              method: 'POST',
              data: {
                post_id: spotlight?.id,
              },
            },
            'sso',
          )
        : undefined
      if (reactionResponse?._data) {
        if (isLoggedIn) {
          setIsLiked(Boolean(reactionResponse?._data?.liked))
          setIsDisliked(Boolean(reactionResponse?._data?.dislike))
        }
      }
    }
    fetchReaction()
  }, [isLoggedIn, spotlight?.id])

  const formatSpotlightContent = (
    content: string,
  ): SpotlightContentFormatted[] => {
    const spotLightContents: SpotlightContentFormatted[] = []
    content
      .split('\n')
      .join('')
      .split(
        /(\[graph\-bar\-chart\])|(\[health\-rendership\-survey\])|(\[obesity\-percentages\])|(\[graph\-most\-viewed\-categories\])|(\[embed\-health\-tool\-[^\]]+\])/,
      )
      .filter((e) => !!e)
      .forEach((text: string) => {
        if (text === '[graph-bar-chart]')
          return spotLightContents.push({
            html: '',
            graphBarChart: {
              direction: 'vertical',
            },
          })
        if (text === '[health-rendership-survey]')
          return spotLightContents.push({
            html: '',
            healthRenderShip: true,
          })
        if (text === '[obesity-percentages]')
          return spotLightContents.push({
            html: '',
            obesity: true,
          })

        if (text === '[graph-most-viewed-categories]')
          return spotLightContents.push({
            html: '',
            mostViewedCategories: true,
          })

        if (text === '[embed-health-tool-bmi]')
          return spotLightContents.push({
            html: '',
            heathTool: 'bmi',
          })

        if (text === '[embed-health-tool-bmr]')
          return spotLightContents.push({
            html: '',
            heathTool: 'bmr',
          })

        if (text === '[embed-health-tool-ovulation]')
          return spotLightContents.push({
            html: '',
            heathTool: 'ovulation',
          })

        if (text === '[embed-health-tool-due-date]')
          return spotLightContents.push({
            html: '',
            heathTool: 'due-date',
          })

        if (text === '[embed-health-tool-heart-rate]')
          return spotLightContents.push({
            html: '',
            heathTool: 'heart-rate',
          })
        if (text === '[embed-health-tool-pregnancy-weight-gain]')
          return spotLightContents.push({
            html: '',
            heathTool: 'pregnancy-weight-gain',
          })
        if (text === '[embed-health-tool-child-growth-chart]')
          return spotLightContents.push({
            html: '',
            heathTool: 'child-growth-chart',
          })
        if (text.includes('[article-yoast-faqs]'))
          return spotLightContents.push({
            html: '',
            faqs: true,
          })

        const splittedHtml: string[] = splitHtml2(text)
        let htmlText = ''

        splittedHtml.forEach((html) => {
          htmlText += html

          if (html.includes('class="flat-after-first-paragraph')) {
            if (htmlText) {
              spotLightContents.push({
                html: _sanitize(htmlText),
              })
              htmlText = ''
            }
            spotLightContents.push({
              html: '',
              isPremium: true,
            })
          }
          if (html.includes('data-embed-type="carousel"')) {
            const id = parseInt(/data-id="(.*?)"/gi.exec(html)?.[1] || '')
            if (htmlText) {
              spotLightContents.push({
                html: _sanitize(htmlText),
              })
              htmlText = ''
            }
            spotLightContents.push({
              html: '',
              carousel: {
                id,
              },
            })
            return
          }
        })
        if (htmlText) {
          spotLightContents.push({
            html: _sanitize(htmlText),
          })
        }
      })
    return spotLightContents
  }

  const splittedContent = useMemo(
    () => formatSpotlightContent(spotlight.content),
    [],
  )

  const isCurrentBookmarked = bookmarkIds?.includes(spotlight?.id)

  const bookmarkArticle = async () => {
    const { id } = currentArticle || {}
    if (id && userInfo?.id) {
      if (!isCurrentBookmarked) {
        const res = await callApiNew(
          PATHS.SSO.CREATE_BOOKMARK,
          {
            method: 'POST',
            data: {
              host_name: currentDomain,
              post_ids: [id],
            },
          },
          'sso',
        )
        if (res?._status === 1) {
          addToBookmark(id)

          dispatch({
            type: actionTypes.ADD_BOOKMARKS,
            payload: {
              bookmarks: [id],
              bookmarkId: id,
            },
          })
        }
      } else {
        const res = await callApiNew(
          PATHS.SSO.DELETE_BOOKMARK,
          {
            method: 'DELETE',
            data: {
              host_name: currentDomain,
              post_ids: [id],
            },
          },
          'sso',
        )
        if (res?._status === 1) {
          removeBookmark(id)
          dispatch({
            type: actionTypes.REMOVE_BOOKMARKS,
            payload: {
              bookmarks: [id],
              bookmarkId: id,
            },
          })
        }
      }
    } else {
      const currentLocation = window.location.href
      const returnUrlEncode = encodeURIComponent(
        `${currentLocation}${
          currentLocation.includes('?') ? '&' : '?'
        }source=bookmark,${id}`,
      )
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          source: `source=bookmark,${id}`,
        })
      } else {
        window.location.href = ENV.NEXT_PUBLIC_SSO
          ? `${SSO_URL}?returnUrl=${returnUrlEncode}`
          : currentLocation
      }
    }
  }

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: {
      result: 'Spotlight',
      redundant: true,
    },
  })
  const mastheadEnable = useMastheadAdEnable(spotlight.adsOnArticles)
  const topbarHeight = isSponsored ? (isMobile ? 105 : 113) : 64
  const premiumIndex = useMemo(
    () => splittedContent.findIndex((content) => content?.isPremium),
    [splittedContent],
  )

  const isShowAllPremium =
    locale === LOCALE.Vietnam ||
    !spotlight.isPremium ||
    premiumIndex < 0 ||
    isLoggedIn

  return (
    <>
      <StyledSuperContentWrapper locale={locale}>
        <AdBlockContainer>
          <MastheadAd
            adUnitPath={adUnitPath}
            className={`spotlight-supercontent-masthead ${spotlight?.type}-masthead`}
            hasPlaceholder={mastheadEnable}
            containerClassName="z-[11]"
          />
        </AdBlockContainer>
        <AnimationBanner
          isMobile={isMobile}
          banners={(spotlight?.banner as SpotlightPageBanner)?.items || []}
          topbarHeight={topbarHeight}
          isPremium={!isShowAllPremium}
          applyH1
        />
        {splittedContent?.map((content: SpotlightContentFormatted, index) => {
          const isPremiumHidden =
            premiumIndex > -1 && index > premiumIndex && !isShowAllPremium
          if (content.isPremium && !isShowAllPremium)
            return <PremiumContentBanner isPortal={false} />
          return (
            <div
              data-hidden={isPremiumHidden}
              style={
                isPremiumHidden
                  ? {
                      height: 0,
                      opacity: 0,
                      transition: 'opacity 0.5s ease-in-out',
                      pointerEvents: 'none',
                      overflow: 'hidden',
                    }
                  : {
                      opacity: 1,
                      transition: 'opacity 0.5s ease-in-out',
                    }
              }
              key={index}
            >
              <Container className="super-content-container">
                <StyledArticleContentWrapperNoAmp
                  className="body-content article-content-wrapper"
                  data-size={fontSize || 'small'}
                  dangerouslySetInnerHTML={{
                    __html: content.html,
                  }}
                />
              </Container>
              {content?.graphBarChart ? (
                <Container className="super-content-container">
                  <AnimationChart
                    isMobile={isMobile}
                    title={f({
                      id: 'superContent.chart.healthTool.title',
                      defaultMessage:
                        'Top health topics Asians look for the most',
                    })}
                    description={f({
                      id: 'superContent.chart.healthTool.description',
                      defaultMessage:
                        'Based on over 9,000 surveyed across Asia in the Hello Health Readership Survey from June to November 2020',
                    })}
                  />
                </Container>
              ) : null}

              {content?.healthRenderShip ? (
                <HealthRendership
                  isMobile={isMobile}
                  topbarHeight={topbarHeight}
                />
              ) : null}

              {content?.obesity ? (
                <ObesityPercentage
                  isMobile={isMobile}
                  topbarHeight={topbarHeight}
                />
              ) : null}

              {content?.faqs ? (
                <Container
                  style={{
                    maxWidth: '768px',
                    textAlign: 'justify',
                    marginBottom: '80px',
                  }}
                >
                  <CollapsibleFaqs
                    isMobile={isMobile}
                    posts={spotlight?.faqs}
                    title={spotlight?.faqsTitle}
                  />
                </Container>
              ) : null}

              {content?.mostViewedCategories ? (
                <MostViewedCategoriesChart
                  isMobile={isMobile}
                  topbarHeight={topbarHeight}
                />
              ) : null}

              {content?.heathTool === 'bmi' ? (
                <Container className="health-tool-container">
                  <BMICardWrapper
                    articleCategories={null}
                    positionType="spotlight"
                  />
                </Container>
              ) : null}

              {content?.heathTool === 'bmr' ? (
                <Container className="health-tool-container">
                  <BMRCardWrapper
                    articleCategories={null}
                    positionType="spotlight"
                  />
                </Container>
              ) : null}

              {content?.heathTool === 'ovulation' ? (
                <Container className="health-tool-container">
                  <OvulationCardWrapper />
                </Container>
              ) : null}

              {content?.heathTool === 'pregnancy-weight-gain' ? (
                <Container className="health-tool-container">
                  <PWGCardWrapper />
                </Container>
              ) : null}

              {content?.heathTool === 'heart-rate' ? (
                <Container className="health-tool-container">
                  <THRCardWrapper />
                </Container>
              ) : null}

              {content?.heathTool === 'due-date' ? (
                <Container className="health-tool-container">
                  <DueDateCardWrapper />
                </Container>
              ) : null}
              {content?.heathTool === 'vaccination-tool' ? (
                <Container className="health-tool-container">
                  <BabyVaccinationCard />
                </Container>
              ) : null}
              {content?.heathTool === 'baby-poop-checking' ? (
                <Container className="health-tool-container">
                  <BabyPoopCard />
                </Container>
              ) : null}
              {content?.heathTool === 'child-growth-chart' ? (
                <Container className="health-tool-container">
                  <BabyGrowthCard />
                </Container>
              ) : null}
              {content?.carousel && (
                <Container className="health-concern-container">
                  <HealthConcern
                    isMobile={isMobile}
                    id={content?.carousel?.id}
                  />
                </Container>
              )}
            </div>
          )
        })}
        <Container
          style={{
            maxWidth: '768px',
            textAlign: 'justify',
            marginBlock: '16px',
          }}
        >
          <ArticleFeedback />
        </Container>
        <StyledActionBarWrapper>
          {isMobile ? (
            <MobileBottomNavigationWrapper />
          ) : (
            <ArticleActionButtonGroup
              articleUrl={`${getHelloSitesUrl(locale)}${spotlight?.permalink}`}
              isBookmarked={isCurrentBookmarked}
              onBookmarkClick={bookmarkArticle}
              // onAskDoctorClick={() => setIsCommentPopupOpen(true)}
              turnOffComment={true}
            />
          )}
          {/* </Container> */}
        </StyledActionBarWrapper>
      </StyledSuperContentWrapper>
      <Container
        style={{
          maxWidth: '768px',
          textAlign: 'justify',
          marginBottom: '80px',
        }}
      >
        {spotlight?.community && (
          <EmbeddedCommunity
            isMobile={isMobile}
            communityId={spotlight?.community?.id}
            titleMember={spotlight?.community?.memberTitle}
            titleGuest={spotlight?.community?.guestTitle}
            descriptionMember={spotlight?.community?.memberDesc}
            descriptionGuest={spotlight?.community?.guestDesc}
          />
        )}
        {spotlight.sources && <Sources sources={spotlight.sources} />}
      </Container>
    </>
  )
}

export default SpotlightSuperContentTemplate
